import * as React from "react";
import {
  Title2,
  Body1,
  Body2,
  Button,
  makeStyles,
  tokens,
  Spinner,
  Badge,
} from "@fluentui/react-components";
import { quickSend } from "../services/api";
import { exportCurrentDocumentAsPdf, getDocumentName } from "../services/document";
import { estimateFieldPositions, removeAllFieldContentControls, FIELD_TYPE_CONFIGS } from "../services/contentControls";
import { Envelope, Organization, PlacedField, Recipient } from "../types";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  section: {
    padding: "12px",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  sectionTitle: {
    marginBottom: "8px",
  },
  recipientItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0",
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
  },
  progress: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    padding: "24px",
  },
});

interface Props {
  organization: Organization;
  recipients: Recipient[];
  signingOrder: "sequential" | "parallel";
  placedFields: PlacedField[];
  onSuccess: (envelope: Envelope) => void;
  onBack: () => void;
}

const ReviewAndSend: React.FC<Props> = ({ organization, recipients, signingOrder, placedFields, onSuccess, onBack }) => {
  const styles = useStyles();
  const [sending, setSending] = React.useState(false);
  const [status, setStatus] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const documentName = getDocumentName();

  const handleSend = async () => {
    setSending(true);
    setError(null);

    try {
      // Estimate field positions before removing content controls
      let fields;
      if (placedFields.length > 0) {
        setStatus("Calculating field positions...");
        fields = await estimateFieldPositions(placedFields);

        setStatus("Cleaning up document...");
        await removeAllFieldContentControls(placedFields);
      }

      setStatus("Exporting document as PDF...");
      const pdfBlob = await exportCurrentDocumentAsPdf();

      setStatus("Sending for signing...");
      const envelope = await quickSend(organization.id, pdfBlob, `${documentName}.pdf`, recipients, {
        name: documentName,
        signingOrder,
        fields,
      });

      onSuccess(envelope);
    } catch (err: any) {
      setError(err.message || "Failed to send. Please try again.");
    } finally {
      setSending(false);
      setStatus("");
    }
  };

  if (sending) {
    return (
      <div className={styles.progress}>
        <Spinner size="large" />
        <Body1>{status}</Body1>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Title2>Review & Send</Title2>

      <div className={styles.section}>
        <Body2 className={styles.sectionTitle} weight="semibold" block>
          Document
        </Body2>
        <Body1>{documentName}.pdf</Body1>
      </div>

      <div className={styles.section}>
        <Body2 className={styles.sectionTitle} weight="semibold" block>
          Organization
        </Body2>
        <Body1>{organization.name}</Body1>
      </div>

      <div className={styles.section}>
        <Body2 className={styles.sectionTitle} weight="semibold" block>
          Signing Order
        </Body2>
        <Body1>{signingOrder === "parallel" ? "Parallel (all at once)" : "Sequential (one after another)"}</Body1>
      </div>

      <div className={styles.section}>
        <Body2 className={styles.sectionTitle} weight="semibold" block>
          Recipients ({recipients.length})
        </Body2>
        {recipients.map((r, i) => (
          <div key={i} className={styles.recipientItem}>
            <div>
              <Body1 block>{r.name}</Body1>
              <Body2>{r.email}</Body2>
            </div>
            <Badge appearance="outline">{r.role}</Badge>
          </div>
        ))}
      </div>

      {placedFields.length > 0 && (
        <div className={styles.section}>
          <Body2 className={styles.sectionTitle} weight="semibold" block>
            Fields ({placedFields.length})
          </Body2>
          {placedFields.map((f, i) => (
            <div key={i} className={styles.recipientItem}>
              <Body1>{FIELD_TYPE_CONFIGS[f.type].label}</Body1>
              <Badge appearance="outline">{recipients[f.recipientIndex]?.name}</Badge>
            </div>
          ))}
        </div>
      )}

      {error && <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Body1>}

      <div className={styles.actions}>
        <Button appearance="secondary" onClick={onBack}>
          Back
        </Button>
        <Button appearance="primary" onClick={handleSend}>
          Send for Signing
        </Button>
      </div>
    </div>
  );
};

export default ReviewAndSend;
